import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { DocumentType, ImageSource } from '@/api/esign-documents';

/**
 * Reconciliation sign-jobs — DataCentral v4 `/reconciliation/sign-jobs` endpoints.
 * Step-based, asynchronous MISA eSign workers: enqueue returns created jobs fast,
 * then the console polls for each job's current `step` + `status` + `step_error`
 * so an operator can SEE where each invoice's document is and retry a stuck one.
 * Auth: UMA RPT via `dataCentralApi` (same as all DC v4 data).
 *
 * Envelope shapes:
 *   List:   { message, result: SignJob[] }  (typed slice → `result`)
 *   Object: { message, result: SignJob }
 */

/* ── Types ──────────────────────────────────────────────────────────────── */

/** Pipeline step the worker is currently on (or finished at). */
export type SignJobStep =
  | 'queued'
  | 'rendering'
  | 'submitting'
  | 'signing'
  | 'storing'
  | 'done'
  | 'failed';

/** Terminal/processing status for the whole job. */
/** `superseded`: the invoice was cancelled / un-merged — the signature no longer applies. */
export type SignJobStatus = 'processing' | 'done' | 'failed' | 'superseded';

/** One async sign-job for one (invoice, document_type) pair. */
export interface SignJob {
  id: number;
  invoice_id: number;
  document_type: DocumentType;
  /** Where the worker is in the render→sign→store pipeline. */
  step: SignJobStep;
  /** Overall job state (poll while `processing`). */
  status: SignJobStatus;
  /** "ở đâu / vì sao" — the error captured at the failing step, if any. */
  step_error?: string | null;
  attempts?: number;
  signed_file_key?: string | null;
  invoice_code?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/* ── Request bodies ───────────────────────────────────────────────────────── */

export interface EnqueueSignJobsBody {
  invoice_ids: number[];
  /** Defaults (backend) to both documents when omitted. */
  document_types?: DocumentType[];
  image_source?: ImageSource;
  /** Sign again a document that is already signed (skipped by default). Costs a MISA signature. */
  force?: boolean;
  /** Backend default true: invoices of 0 đồng are not signed. false = sign them anyway. */
  skip_zero?: boolean;
}

/** `GET /reconciliation/sign-jobs/:id/file` — temporary links to a job's signed PDF. */
export interface SignedFileLinks {
  /** Opens the PDF in the browser. */
  file_url: string;
  /** Forces a PDF download. */
  file_download_url: string;
  file_url_expires_at?: string | null;
}

/* ── Internal envelopes ─────────────────────────────────────────────────── */

interface SignJobListResponse {
  message: string;
  results?: SignJob[];
  result?: SignJob[];
}

/* ── Query key factory ───────────────────────────────────────────────────── */

export const signJobKeys = {
  all: ['reconciliation', 'sign-jobs'] as const,
  list: (invoiceIds: number[], status?: SignJobStatus | '') =>
    ['reconciliation', 'sign-jobs', 'list', [...invoiceIds].sort((a, b) => a - b), status ?? ''] as const,
  detail: (id: number | string) => ['reconciliation', 'sign-jobs', 'detail', id] as const,
};

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/**
 * `POST /reconciliation/sign-jobs` — enqueue sign-jobs for a set of invoices.
 * Returns the created jobs fast; the worker processes them asynchronously.
 * USER must trigger this — never called automatically.
 */
export function useEnqueueSignJobs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: EnqueueSignJobsBody): Promise<SignJob[]> => {
      const res = await dataCentralApi.post<{ message: string; results?: SignJob[]; result?: SignJob[] }>(
        `/reconciliation/sign-jobs`,
        body,
      );
      return res.data.results ?? res.data.result ?? [];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: signJobKeys.all });
    },
  });
}

/**
 * `GET /reconciliation/sign-jobs?invoice_ids=1,2,3&status=` — jobs for a set of
 * invoices, for polling. While `poll` is on, refetches every 3s as long as ANY
 * returned job is still `processing`; stops once all jobs reach a terminal state.
 * Envelope: { message, result: SignJob[] } (typed slice → `result`; `results` kept as fallback)
 */
export function useSignJobs(
  invoiceIds: number[],
  opts?: { poll?: boolean; status?: SignJobStatus | ''; enabled?: boolean },
) {
  return useQuery({
    queryKey: signJobKeys.list(invoiceIds, opts?.status),
    queryFn: async (): Promise<SignJob[]> => {
      const query = qs.stringify({
        invoice_ids: invoiceIds.join(','),
        ...(opts?.status ? { status: opts.status } : {}),
      });
      const res = await dataCentralApi.get<SignJobListResponse>(
        `/reconciliation/sign-jobs?${query}`,
      );
      return res.data.results ?? res.data.result ?? [];
    },
    enabled: invoiceIds.length > 0 && (opts?.enabled ?? true),
    // Poll while any job is still processing; stop once all are terminal.
    refetchInterval: opts?.poll
      ? (query) =>
          (query.state.data ?? []).some((j) => j.status === 'processing') ? 3000 : false
      : false,
  });
}

/**
 * `GET /reconciliation/sign-jobs/:id` — one job (detail / targeted poll).
 * Envelope: { message, result: SignJob }
 */
export function useSignJob(id: number | string | null, opts?: { enabled?: boolean; poll?: boolean }) {
  return useQuery({
    queryKey: signJobKeys.detail(id ?? 0),
    queryFn: async (): Promise<SignJob> => {
      const res = await dataCentralApi.get<{ message: string; result: SignJob }>(
        `/reconciliation/sign-jobs/${id}`,
      );
      return res.data.result;
    },
    enabled: Boolean(id) && (opts?.enabled ?? true),
    refetchInterval: opts?.poll
      ? (query) => (query.state.data?.status === 'processing' ? 3000 : false)
      : false,
  });
}

/**
 * `POST /reconciliation/sign-jobs/:id/retry` — re-run a failed / stuck job from
 * its failing step. USER must trigger this — never called automatically.
 */
export function useRetrySignJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string): Promise<SignJob> => {
      const res = await dataCentralApi.post<{ message: string; result: SignJob }>(
        `/reconciliation/sign-jobs/${id}/retry`,
      );
      return res.data.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: signJobKeys.all });
    },
  });
}

/**
 * `POST /reconciliation/sign-jobs/:id/cancel` — stop a job that is not signed yet
 * (stuck or no longer wanted). The document reads as not signed again. 409 once MISA
 * has signed. USER must trigger this — never called automatically.
 */
export function useCancelSignJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string): Promise<SignJob> => {
      const res = await dataCentralApi.post<{ message: string; result: SignJob }>(
        `/reconciliation/sign-jobs/${id}/cancel`,
      );
      return res.data.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: signJobKeys.all });
    },
  });
}

/**
 * `GET /reconciliation/sign-jobs/:id/file` — open or download the signed PDF of one
 * job. The links are short-lived, so they are fetched on click rather than listed.
 * The view tab is opened before the request so the browser does not block it as a popup.
 */
export function useOpenSignedFile() {
  return useMutation({
    mutationFn: async ({ id, mode }: { id: number; mode: 'view' | 'download' }): Promise<void> => {
      const tab = mode === 'view' ? window.open('', '_blank') : null;
      try {
        const res = await dataCentralApi.get<{ message: string; result: SignedFileLinks }>(
          `/reconciliation/sign-jobs/${id}/file`,
        );
        const url = mode === 'view' ? res.data.result.file_url : res.data.result.file_download_url;
        if (tab) {
          tab.opener = null;
          tab.location.href = url;
        } else {
          window.location.assign(url);
        }
      } catch (err) {
        tab?.close();
        throw err;
      }
    },
  });
}
